import React, {Component, Fragment} from 'react'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import { CountryActions, CountryGroupActions, DealSheetActions, CurrencyActions, SocialMediaWebsitesActions, ServiceActions, IssuerActions, DownloadActions } from 'actionsets'
import { SnackbarActions, GlobalLoaderActions } from 'actionsets'
import Dependent, { GlobalLoader } from 'containers/shared/Dependent'
import {DealForm} from 'components/deal_sheet'
import {mergeCollectionAndDefaults, mergeStatePropsAndDefaults, uuid} from 'utils'
import NavigationConfirmation from 'components/NavigationConfirmation'

export class DealInfo extends Component {
  constructor(props) {
    super(props)

    DealSheetActions.bindActions(this)
    CountryActions.bindActions(this, 'country')
    CountryGroupActions.bindActions(this, 'countryGroup')
    CurrencyActions.bindActions(this, 'currency')
    SocialMediaWebsitesActions.bindActions(this, 'socialMediaWebsite')
    ServiceActions.bindActions(this, 'service')
    IssuerActions.bindActions(this, 'issuer')
    DownloadActions.bindActions(this, 'downloads')
    SnackbarActions.bindActions(this, 'snackbar')
    GlobalLoaderActions.bindActions(this, 'loader')

    this.state = {dealSheet: {}}
  }

  dependsOn(){
    return Promise.all([
      this.actions.country.index(),
      this.actions.countryGroup.index(),
      this.actions.currency.index(),
      this.actions.socialMediaWebsite.index(),
      this.actions.service.index()
    ])
  }

  dependenciesMet(){
    return this.props.dependentRequests.length === 0
  }

  handleCancel = () => {
    this.props.history.goBack()
  }

  handleChange = dealSheet => {
    if (dealSheet.downloads.some(d1 => dealSheet.downloads.some(d2 => d1 !== d2 && d1.name === d2.name)))
      this.actions.snackbar.error("You cannot add multiple documents with the same name.")
    else if (dealSheet.teamMembers.some(m1 => dealSheet.teamMembers.some(m2 => m1 !== m2 && m1.title === m2.title)))
      this.actions.snackbar.error("You cannot add multiple members with the same title.")
    else
      this.setState({dealSheet, dirty: true})
  }

  get dealSheet() {
    const dealSheet = this.sanitizeDealSheet({...this.props.dealSheet, ...this.state.dealSheet})
    const { isRestrictedToInvestors } = this.state.dealSheet

    return {
      ...dealSheet,
      isRestrictedToInvestors: isRestrictedToInvestors === undefined ? (this.defaultIsRestrictedToInvestors(dealSheet)) : isRestrictedToInvestors,
      currency: this.props.currencies.find(c => c.id === (this.state.dealSheet.currencyId || this.props.dealSheet.currencyId)),
      teamMembers: mergeStatePropsAndDefaults(this.state.dealSheet.teamMembers, this.props.dealSheet.teamMembers, defaults.teamMembers, 'title'),
      downloads: mergeStatePropsAndDefaults(this.state.dealSheet.downloads, this.props.dealSheet.downloads, defaults.downloads, 'key'),
      partners: mergeStatePropsAndDefaults(this.state.dealSheet.partners, this.props.dealSheet.partners, defaults.partners, 'key'),
      galleryImages: mergeStatePropsAndDefaults(this.state.dealSheet.galleryImages, this.props.dealSheet.galleryImages, defaults.galleryImages, 'key'),
      fundsUses: mergeCollectionAndDefaults(dealSheet.fundsUses, defaults.fundsUses, 'key'),
      socialMediaLinks: mergeCollectionAndDefaults(dealSheet.socialMediaLinks, this.defaultSocialMediaLinks, 'socialMediaWebsiteId'),
      legalAdvisers: dealSheet.legalAdvisers.length > 0 ? dealSheet.legalAdvisers : [{default: true, key: uuid()}],
    }
  }

  defaultIsRestrictedToInvestors = dealSheet => {
    return dealSheet.isRestrictedToCountries && dealSheet.allowedCountries.length === 0
  }

  get defaultSocialMediaLinks() {
    return this.props.socialMediaWebsites.map(smw => ({socialMediaWebsiteId: smw.id, name: smw.name, linkUrl: ''}))
  }

  sanitizeDealSheet = (dealSheet) => {
    const acceptedCurrencies = dealSheet.acceptedCurrencies || [dealSheet.currency]
    const allowedCountries = dealSheet.isRestrictedToInvestors ? [] : dealSheet.allowedCountries
    const isRestrictedToCountries = dealSheet.isRestrictedToInvestors ? true : dealSheet.isRestrictedToCountries

    return {...dealSheet, acceptedCurrencies, allowedCountries, isRestrictedToCountries}
  }

  handleNavigationSave = async() => {
    try {
      await this.submit()
    }
    catch (ex) {
      if (ex.code !== 'SAVED_BUT_INVALID')
        throw ex
    }
  }

  submit = async (event, reason) => {
    const sanitizedDealSheet = this.sanitizeDealSheet(this.dealSheet)

    try {
      if (this.state.dirty) {
        await this.save(sanitizedDealSheet)
        this.actions.snackbar.show('Deal sheet updated.')
      }
      if (event) {
        if (event === 'publish' && sanitizedDealSheet.paymentDestination.status !== 'ready')
          this.actions.snackbar.error('Wallet not ready.')
        else {
          await this.submitEvent(event, reason, sanitizedDealSheet)
          this.actions.snackbar.show('Deal sheet updated.')
        }
      }
    }
    catch (ex) {
      if (ex.code !== 'SAVED_BUT_INVALID')
        this.actions.snackbar.error('Deal sheet could not be updated.')
      throw ex
    }

    if (this.state.dealSheet) {
      delete this.state.dealSheet.logo
      delete this.state.dealSheet.emblem
      delete this.state.dealSheet.fundsUseInfographic
      if (this.state.dealSheet.companyInfo)
        delete this.state.dealSheet.companyInfo.certificationOfIncorporation
      if (this.state.dealSheet.teamMembers)
        this.state.dealSheet.teamMembers.forEach(m => delete m.photo)
      if (this.state.dealSheet.partners)
        this.state.dealSheet.partners.forEach(l => delete l.logo)
      if (this.state.dealSheet.galleryImages)
        this.state.dealSheet.galleryImages.forEach(l => delete l.image)
      if (this.state.dealSheet.downloads)
        this.state.dealSheet.downloads.forEach(d => delete d.attachment)
    }

    this.setState({dirty: false})
  }

  submitEvent = async (event, reason, dealSheet) => {
    await this.actions.createEvent(dealSheet.id, { name: event, reason })
    this.props.history.push(`/deal_sheets/${dealSheet.id}/summary`)

    window.scrollTo(0,0)
  }

  save = async(dealSheet) => {
    let id
    try {
      if (dealSheet.id) {
        id = dealSheet.id
        await this.actions.update(dealSheet)
      }
      else {
        id = (await this.actions.create(dealSheet)).data.id
        this.props.history.replace(`/deal_sheets/${id}/deal_info`)
      }
    }
    finally {
      if (id) {
        await this.uploadFiles(id, dealSheet)
      }
    }
  }

  uploadFiles = async(id, dealSheet) => {
    await this.actions.updateFiles({...dealSheet, id})
  }

  combineErrors = (...errors) => {
    const result = {}
    errors.filter(e => e && e.meta).forEach(error => {
      Object.keys(error.meta).forEach(k => {
        result[k] = error.meta[k]
      })
    })
    return result
  }

  get errors() {
    const {update, create, updateFiles, createEvent} = this.props.errors
    const save = this.props.dealSheet.id ? update : create
    return this.combineErrors(save, updateFiles, createEvent)
  }

  accountProvider = (query) =>
    this.actions.issuer.search(query)

  render = () => {
    const {services, currencies, countries, countryGroups} = this.props
    const {dirty} = this.state
    return (
      <Fragment>
        <NavigationConfirmation when={dirty} message='Your deal has unsaved changes.' onSave={this.handleNavigationSave} />
        <DealForm
          dealSheet={this.dealSheet} errors={this.errors}
          onChange={this.handleChange} onSave={this.submit} actions={this.formActions}
          accountProvider={this.accountProvider} onCancel={this.handleCancel}
          {...{services, currencies, countries, countryGroups, dirty}}
        />
      </Fragment>
    )
  }
}

const defaults = {
  teamMembers: [{title: 'CEO', name: '', email: '', url: '', default: true}],
  downloads: [
    {name: 'Whitepaper', key: 'whitepaper', attachment: null, description: '', documentUrl: null, default: true},
    {name: 'Contract/SAFT', key: 'saft', attachment: null, description: '', documentUrl: null, default: true}
  ],
  fundsUses: [
    {category: 'Infrastructure', default: true, key: 'Infrastructure'},
    {category: 'Product Development', default: true, key: 'Product Development'},
    {category: 'Marketing/Sales', default: true, key: 'Marketing/Sales'},
    {category: 'Operations', default: true, key: 'Operations'},
    {category: 'Legal', default: true, key: 'Legal'}
  ],
  partners: [],
  galleryImages: []
}

function stateToPropsMapper({dealSheets, countries, countryGroups, currencies, socialMediaWebsites, services}) {
  const dependentRequests = [countries, countryGroups, currencies, socialMediaWebsites, services]
                      .map(state => state.requests).reduce((acc, requests) => [...acc, ...requests], [])

  return {
    errors: dealSheets.errors,
    dealSheet: dealSheets.dealSheet,
    countries: countries.countries,
    countryGroups: countryGroups.countryGroups,
    currencies: currencies.currencies,
    socialMediaWebsites: socialMediaWebsites.sites,
    services: services.services,
    dependentRequests
  }
}

export default connect(stateToPropsMapper)(withRouter((Dependent({loader: GlobalLoader})(DealInfo))))